<template>
  <PartnerListWidget :href="actionLink" @click="onLinkClick">
    <template #icon>
      <HgIcon icon="Map" size="large" theme="secondary" />
    </template>
    <template #content>
      <span data-test="ubs-municipality-guide">
        {{ $t('integrations.ubsMunicipalityGuide.message') }}
        <span :class="$style.cta">
          {{ $t('integrations.ubsMunicipalityGuide.linksText', { zipCode: postalCode, locality }) }}
        </span>
      </span>
    </template>
  </PartnerListWidget>
</template>

<script lang="ts" setup>
import { HgIcon } from '@smg-real-estate/domus';
import { Address, OfferType } from '@smg-real-estate/hg-rets-schema';
import { computed, onMounted, ref } from 'vue';

import PartnerListWidget from '@/components/PartnerIntegrations/PartnerListWidget/PartnerListWidget.vue';
import { InteractionGoal } from '@/helpers/GTM/GTMParameters';

import { useI18n } from 'vue-i18n';
import { getActionLink, getCommuneIdByPostalCode } from './functions';
import { fetchUbsGuideMappings } from './services';

defineOptions({
  name: 'UbsMunicipalityGuide',
});

const { t, locale } = useI18n();

interface Props {
  address: Address;
  offerType: OfferType;
}
const props = defineProps<Props>();

const emit = defineEmits<{
  trackPartnerLink: [link: string, goal: InteractionGoal];
}>();

const communeId = ref<number | null>(null);

const actionLink = computed((): string => {
  const url = t('integrations.ubsMunicipalityGuide.href').toString();
  return getActionLink({
    url,
    locale: locale.value,
    offerType: props.offerType,
    postalCode: props.address?.postalCode ?? '',
    communeId: communeId.value,
  });
});

const postalCode = computed((): string => {
  return props.address?.postalCode ?? '';
});

const locality = computed((): string => {
  return props.address?.locality ?? '';
});

const getAsyncData = async () => {
  try {
    const mapPostalCodes = await fetchUbsGuideMappings();
    communeId.value = getCommuneIdByPostalCode(mapPostalCodes, postalCode.value);
  } catch (_) {
    communeId.value = null;
  }
};

const onLinkClick = () => {
  emit('trackPartnerLink', actionLink.value, InteractionGoal.LOCATION_DEMOGRAPHY_TAXES_OVERVIEW);
};

onMounted(async () => {
  await getAsyncData();
});
</script>
<style lang="scss" module>
@import '@/assets/variables';

.cta {
  color: var(--base-color-link);

  @include font-bold;
}
</style>
